import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import styles from './ProductForm.module.css';
import ProductAttributesModal from './ProductAttributesModal';

const defaultForm = {
  name: '',
  price: '',
  original_price: '',
  cost_price: '',
  description: '',
  short_description: '',
  stock_quantity: '',
  category_id: '',
  brand: '',
  weight: '',
  dimensions: '',
  is_active: true,
  is_featured: false,
  is_prime: false,
  is_primary: true,
};

const ProductForm = ({ categories, onProductCreated, product }) => {
  const [form, setForm] = useState(defaultForm);
  const [existingImages, setExistingImages] = useState([]);
  // Actualiza el formulario si se recibe un producto (edición)
  useEffect(() => {
    if (product) {
      setForm({
        ...defaultForm,
        ...product,
        price: product.price ?? '',
        original_price: product.original_price ?? '',
        cost_price: product.cost_price ?? '',
        stock_quantity: product.stock_quantity ?? '',
        weight: product.weight ?? '',
        dimensions: product.dimensions ? JSON.stringify(product.dimensions) : '',
        category_id: product.category_id ?? '',
        is_active: product.is_active ?? true,
        is_featured: product.is_featured ?? false,
        is_prime: product.is_prime ?? false,
        is_primary: true,
      });
      // Cargar imágenes existentes desde product_images (GET)
      apiService.getProductImages(product.id).then(data => {
        setExistingImages(data.images || []);
      });
      
      // Cargar atributos del producto (especificaciones y variantes)
      apiService.getProductAttributes(product.id).then(data => {
        const attrs = data.attributes || [];
        
        // Separar en especificaciones y variantes
        const specs = attrs.filter(a => a.category === 'specification');
        const variants = attrs.filter(a => a.category === 'variant');
        
        // Cargar especificaciones con sus valores
        const specsWithValues = specs.map(attr => ({
          id: attr.id,
          name: attr.name,
          type: attr.type,
          category: attr.category,
          value: attr.product_attribute_values?.[0]?.value || ''
        }));
        setSpecificationAttributes(specsWithValues);
        
        // Cargar variantes existentes del producto
        apiService.request(`/products/${product.id}/variants`).then(variantsResponse => {
          const existingVariants = variantsResponse.variants || [];
          console.log('Variantes existentes con stock/precio:', existingVariants);
          
          if (existingVariants.length > 0 && variants.length > 0) {
            // Mapear variantes existentes de vuelta a selectedVariantAttributes
            const variantsByAttribute = new Map();
            const pricesMap = {};
            const stocksMap = {};
            
            // Agrupar opciones por atributo
            variants.forEach(attr => {
              variantsByAttribute.set(attr.id, {
                ...attr,
                options: []
              });
            });
            
            // Para cada variante existente, extraer sus opciones y valores
            existingVariants.forEach(existingVariant => {
              if (existingVariant.product_variant_attributes) {
                const optionIds = [];
                
                existingVariant.product_variant_attributes.forEach(va => {
                  const attrId = va.product_attributes?.id;
                  optionIds.push(va.attribute_options?.id);
                  
                  if (attrId && variantsByAttribute.has(attrId)) {
                    const attr = variantsByAttribute.get(attrId);
                    // Buscar o crear entrada para esta opción
                    let optionEntry = attr.options.find(o => o.optionId === va.attribute_options?.id);
                    if (!optionEntry) {
                      optionEntry = {
                        optionId: va.attribute_options?.id,
                        value: va.attribute_options?.value,
                        displayName: va.attribute_options?.display_name || va.attribute_options?.value,
                        colorHex: va.attribute_options?.color_hex
                      };
                      attr.options.push(optionEntry);
                    }
                  }
                });
                
                // Guardar stock y precio por combinación única
                if (optionIds.length > 0) {
                  const comboKey = optionIds.sort().join('-');
                  stocksMap[comboKey] = existingVariant.stock_quantity;
                  if (existingVariant.price !== null) {
                    pricesMap[comboKey] = existingVariant.price;
                  }
                }
              }
            });
            
            // Convertir map a array con opciones mapeadas
            const mappedVariants = Array.from(variantsByAttribute.values()).map(attr => ({
              attributeId: attr.id,
              name: attr.name,
              type: attr.type,
              options: attr.options
            }));
            
            console.log('Variantes mapeadas para UI:', mappedVariants);
            console.log('Precios por combinación:', pricesMap);
            console.log('Stocks por combinación:', stocksMap);
            
            setSelectedVariantAttributes(mappedVariants);
            setVariantCombinationPrices(pricesMap);
            setVariantCombinationStocks(stocksMap);
          }
        }).catch(err => console.error('Error cargando variantes:', err));
        
      }).catch(err => {
        console.error('Error cargando atributos del producto:', err);
      });
    } else {
      setForm(defaultForm);
      setExistingImages([]);
      setSpecificationAttributes([]);
      setSelectedVariantAttributes([]);
      setVariantCombinationPrices({});
      setVariantCombinationStocks({});
    }
  }, [product]);
  const [imageFiles, setImageFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAttrModal, setShowAttrModal] = useState(false);
  const [attributes, setAttributes] = useState([]);
  const [allAttributes, setAllAttributes] = useState([]); // Atributos disponibles de la BD
  const [specificationAttributes, setSpecificationAttributes] = useState([]); // Atributos informativos
  const [selectedVariantAttributes, setSelectedVariantAttributes] = useState([]); // Atributos variables seleccionados para este producto
  const [variantCombinationPrices, setVariantCombinationPrices] = useState({}); // Map de combinaciones a {price, stock}
  const [variantCombinationStocks, setVariantCombinationStocks] = useState({}); // Map de combinaciones a stock
  // productId is intentionally not stored separately - use product?.id when needed

  // Cargar atributos disponibles al montar
  useEffect(() => {
    const loadAttributes = async () => {
      try {
        const data = await apiService.getAllProductAttributes();
        console.log('Atributos cargados:', data);
        setAllAttributes(data.attributes || []);
      } catch (err) {
        console.error('Error cargando atributos:', err);
        setAllAttributes([]);
      }
    };
    loadAttributes();
  }, []);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setForm({
      ...form,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleImageChange = (e) => {
    setImageFiles(Array.from(e.target.files));
  };

  const handleDeleteExistingImage = async (imageId) => {
    const image = existingImages.find(img => img.id === imageId);
    if (!image) return;
    try {
      await apiService.request(`/products/${product.id}/images`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: apiService.token ? `Bearer ${apiService.token}` : undefined
        },
        body: JSON.stringify({ imageId: image.id, cloudflareUrl: image.cloudflare_url })
      });
      setExistingImages(existingImages.filter(img => img.id !== imageId));
    } catch (err) {
      setError('No se pudo borrar la imagen: ' + (err.message || 'Error desconocido'));
    }
  };

  // Si es edición, carga los atributos del producto
  useEffect(() => {
    if (product?.id) {
      apiService.getProductAttributes(product.id)
        .then(data => setAttributes(data.attributes || []))
        .catch(() => setAttributes([]));
    }
  }, [product]);

  const addSpecificationAttribute = () => {
    setSpecificationAttributes([
      ...specificationAttributes,
      { attributeId: '', value: '' }
    ]);
  };

  const updateSpecificationAttribute = (index, field, value) => {
    const updated = [...specificationAttributes];
    updated[index] = { ...updated[index], [field]: value };
    setSpecificationAttributes(updated);
  };

  const removeSpecificationAttribute = (index) => {
    setSpecificationAttributes(specificationAttributes.filter((_, i) => i !== index));
  };

  // Funciones para atributos variables
  const toggleVariantAttribute = async (attributeId) => {
    console.log('toggleVariantAttribute llamada con:', attributeId);
    console.log('allAttributes:', allAttributes);
    
    const exists = selectedVariantAttributes.some(attr => String(attr.attributeId) === String(attributeId));
    
    if (exists) {
      // Remover atributo
      setSelectedVariantAttributes(
        selectedVariantAttributes.filter(attr => String(attr.attributeId) !== String(attributeId))
      );
    } else {
      // Agregar atributo con sus opciones
      const attribute = allAttributes.find(a => String(a.id) === String(attributeId) && a.category === 'variant');
      console.log('Atributo encontrado:', attribute);
      
      if (attribute) {
        // Cargar opciones del atributo
        try {
          const optionsResponse = await apiService.getAttributeOptions(attributeId);
          const options = optionsResponse.options || [];
          console.log('Opciones cargadas:', options);
          
          // Crear entrada para cada opción con stock (inicializar vacío, no con 0)
          const newVariantAttr = {
            attributeId,
            name: attribute.name,
            type: attribute.type,
            options: options.map(opt => ({
              optionId: opt.id,
              value: opt.value,
              displayName: opt.display_name || opt.value,
              colorHex: opt.color_hex,
              stock: '' // Stock inicial vacío, no con 0
            }))
          };
          
          setSelectedVariantAttributes([...selectedVariantAttributes, newVariantAttr]);
          console.log('Atributo agregado:', newVariantAttr);
        } catch (err) {
          console.error('Error cargando opciones:', err);
        }
      } else {
        console.error('Atributo no encontrado con ID:', attributeId);
      }
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validar que todas las variantes tengan stock asignado (permitir 0)
      if (selectedVariantAttributes.length > 0) {
        // Generar todas las combinaciones posibles para validar que todas tengan stock
        const generateCombinations = (attributes) => {
          if (attributes.length === 0) return [];
          if (attributes.length === 1) {
            return attributes[0].options.map(opt => [opt.optionId]);
          }
          const combinations = [];
          const restCombinations = generateCombinations(attributes.slice(1));
          attributes[0].options.forEach(opt => {
            if (restCombinations.length === 0) {
              combinations.push([opt.optionId]);
            } else {
              restCombinations.forEach(combo => {
                combinations.push([opt.optionId, ...combo]);
              });
            }
          });
          return combinations;
        };
        
        const combinations = generateCombinations(selectedVariantAttributes);
        
        // Validar que cada combinación tenga stock asignado
        const hasEmptyStock = combinations.some(combo => {
          const comboKey = combo.sort().join('-');
          const stock = variantCombinationStocks[comboKey];
          return stock === '' || stock === null || typeof stock === 'undefined';
        });
        
        if (hasEmptyStock) {
          setError('⚠️ Debes asignar stock a todas las opciones de variantes (0 es válido)');
          setLoading(false);
          return;
        }
      }

      // 1. Crear o actualizar producto (sin campos de imágenes)
      const productData = {
        ...(() => {
          const { is_primary, ...rest } = form;
          return rest;
        })(),
        price: parseFloat(form.price),
        original_price: form.original_price ? parseFloat(form.original_price) : null,
        cost_price: form.cost_price ? parseFloat(form.cost_price) : null,
        stock_quantity: parseInt(form.stock_quantity),
        weight: form.weight ? parseFloat(form.weight) : null,
        dimensions: form.dimensions ? JSON.parse(form.dimensions) : null
      };
      let prodData;
      if (product?.id) {
        prodData = await apiService.updateProduct(product.id, productData);
      } else {
        prodData = await apiService.createProduct(productData);
      }
      if (!prodData.id) throw new Error(prodData.error || 'Error al guardar producto');

      // 2. Subir imágenes solo si hay cambios y ya existe el id
      if (imageFiles.length > 0) {
        for (let i = 0; i < imageFiles.length; i++) {
          const file = imageFiles[i];
          // La primera imagen será primaria solo si no hay imágenes existentes
          const isFirstImage = i === 0 && existingImages.length === 0;
          try {
            await apiService.uploadProductImage(prodData.id, file, isFirstImage);
          } catch (err) {
            console.error('Error subiendo imagen:', err);
            setError(`Error subiendo imagen: ${err.message}`);
          }
        }
      }
      // Eliminar imágenes (puedes implementar DELETE en el backend si lo necesitas)
      // Aquí solo se marcan para eliminar, pero no se hace la petición DELETE

      // 3. Guardar atributos personalizados
      if (attributes.length > 0) {
        await apiService.createProductAttribute(product?.id || prodData.id, attributes);
      }

      // 4. Guardar atributos informativos (especificaciones)
      if (specificationAttributes.length > 0) {
        for (const spec of specificationAttributes) {
          if (spec.attributeId && spec.value) {
            await apiService.createProductAttribute(product?.id || prodData.id, [{
              attributeId: spec.attributeId,
              value: spec.value,
              type: 'specification'
            }]);
          }
        }
      }

      // 5. Crear variantes con sus stocks
      if (selectedVariantAttributes.length > 0) {
        // Generar todas las combinaciones posibles de opciones
        const generateCombinations = (attributes) => {
          if (attributes.length === 0) return [];
          if (attributes.length === 1) {
            return attributes[0].options.map(opt => [{
              attributeId: attributes[0].attributeId,
              optionId: opt.optionId,
              stock: opt.stock
            }]);
          }
          const combinations = [];
          const restCombinations = generateCombinations(attributes.slice(1));
          attributes[0].options.forEach(opt => {
            if (restCombinations.length === 0) {
              combinations.push([{
                attributeId: attributes[0].attributeId,
                optionId: opt.optionId,
                stock: opt.stock
              }]);
            } else {
              restCombinations.forEach(combo => {
                combinations.push([{
                  attributeId: attributes[0].attributeId,
                  optionId: opt.optionId,
                  stock: opt.stock
                }, ...combo]);
              });
            }
          });
          return combinations;
        };

        const combinations = generateCombinations(selectedVariantAttributes);
        for (const combo of combinations) {
          // Crear clave única para esta combinación
          const comboKey = combo.map(item => item.optionId).sort().join('-');
          // Obtener stock y precio de los mapas por esta combinación
          // stockValue: si existe en el mapa, usarlo, si no, usar el valor del input de la opción
          let stockValue = typeof variantCombinationStocks[comboKey] !== 'undefined' ? variantCombinationStocks[comboKey] : combo[0].stock;
          if (stockValue === '' || stockValue === null || typeof stockValue === 'undefined') stockValue = 0;
          // priceValue: si existe en el mapa, usarlo, si no, enviar null para que el backend use el precio base
          let priceValue = typeof variantCombinationPrices[comboKey] !== 'undefined' ? variantCombinationPrices[comboKey] : null;
          try {
            await apiService.request(`/products/${prodData.id}/variants`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: apiService.token ? `Bearer ${apiService.token}` : undefined
              },
              body: JSON.stringify({
                stock_quantity: stockValue,
                price: priceValue,
                attributes: combo.map(c => ({
                  attribute_id: c.attributeId,
                  option_id: c.optionId
                }))
              })
            });
          } catch (err) {
            console.error('Error creando variante:', err);
          }
        }
      }

      setSuccess(product?.id ? 'Producto actualizado correctamente' : 'Producto creado correctamente');
      setForm(defaultForm);
      setImageFiles([]);
      setExistingImages([]);
      setAttributes([]);
      setSpecificationAttributes([]);
      setSelectedVariantAttributes([]);
      setVariantCombinationPrices({});
      setVariantCombinationStocks({});
      if (onProductCreated) onProductCreated(prodData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={styles.formContainer}>
        <div className={styles.formHeader}>
          <h2 className={styles.formTitle}>{product ? 'Editar Producto' : 'Nuevo Producto'}</h2>
        </div>
        
        <form className={styles.form} onSubmit={handleSubmit}>
          {error && <div className={styles.error}>{error}</div>}
          {success && <div className={styles.success}>{success}</div>}

          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Información básica</h3>
            <div className={styles.gridTwo}>
              <label className={styles.label}>
                Nombre
                <input name="name" value={form.name} onChange={handleChange} required className={styles.input} />
              </label>
              <label className={styles.label}>
                Categoría
                <select
                  name="category_id"
                  value={form.category_id}
                  onChange={handleChange}
                  required
                  className={styles.select}
                >
                  <option value="">Selecciona una categoría</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className={styles.gridThree}>
              <label className={styles.label}>
                Precio
                <input name="price" type="number" step="0.01" value={form.price} onChange={handleChange} required className={styles.input} />
              </label>
              <label className={styles.label}>
                Stock
                <input name="stock_quantity" type="number" value={form.stock_quantity} onChange={handleChange} required className={styles.input} />
              </label>
              <label className={styles.label}>
                Marca
                <input name="brand" value={form.brand} onChange={handleChange} className={styles.input} />
              </label>
            </div>
          </div>

          <details className={styles.panel} open>
            <summary className={styles.panelSummary}>
              <span>Descripción</span>
              <span className={styles.panelMeta}>{form.description ? 'Listo' : 'Pendiente'}</span>
            </summary>
            <div className={styles.panelContent}>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>
                  Descripción completa
                  <textarea name="description" value={form.description} onChange={handleChange} required className={styles.textarea} rows="5" />
                </label>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>
                  Descripción corta
                  <input name="short_description" value={form.short_description} onChange={handleChange} className={styles.input} />
                </label>
              </div>
            </div>
          </details>

          <details className={styles.panel}>
            <summary className={styles.panelSummary}>
              <span>Precios avanzados</span>
              <span className={styles.panelMeta}>
                {form.original_price || form.cost_price ? 'Configurado' : 'Opcional'}
              </span>
            </summary>
            <div className={styles.panelContent}>
              <div className={styles.gridTwo}>
                <label className={styles.label}>
                  Precio original
                  <input name="original_price" type="number" step="0.01" value={form.original_price} onChange={handleChange} className={styles.input} />
                </label>
                <label className={styles.label}>
                  Costo
                  <input name="cost_price" type="number" step="0.01" value={form.cost_price} onChange={handleChange} className={styles.input} />
                </label>
              </div>
            </div>
          </details>

          <details className={styles.panel}>
            <summary className={styles.panelSummary}>
              <span>Logística</span>
              <span className={styles.panelMeta}>
                {form.weight || form.dimensions ? 'Configurado' : 'Opcional'}
              </span>
            </summary>
            <div className={styles.panelContent}>
              <div className={styles.gridTwo}>
                <label className={styles.label}>
                  Peso (kg)
                  <input name="weight" type="number" step="0.01" value={form.weight} onChange={handleChange} className={styles.input} />
                </label>
                <label className={styles.label}>
                  Dimensiones (JSON)
                  <input name="dimensions" value={form.dimensions} onChange={handleChange} placeholder='{"width":10,"height":5,"depth":2}' className={styles.input} />
                </label>
              </div>
            </div>
          </details>

          <details className={styles.panel}>
            <summary className={styles.panelSummary}>
              <span>Imágenes</span>
              <span className={styles.panelMeta}>
                {(existingImages.length + imageFiles.length) > 0 ? `${existingImages.length + imageFiles.length}` : 'Opcional'}
              </span>
            </summary>
            <div className={styles.panelContent}>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>
                  Agregar imágenes
                  <input type="file" accept="image/*" multiple onChange={handleImageChange} className={styles.fileInput} />
                </label>
              </div>
              {existingImages.length > 0 && (
                <div className={styles.imagePreviewList}>
                  <span className={styles.imageLabel}>Imágenes actuales</span>
                  <div className={styles.imagePreviewGrid}>
                    {existingImages.map(img => (
                      <div key={img.id} className={styles.imagePreviewItem}>
                        <img src={img.cloudflare_url} alt="Producto" className={styles.imagePreview} />
                        <button type="button" className={styles.deleteImageBtn} onClick={() => handleDeleteExistingImage(img.id)}>
                          Eliminar
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </details>

          <details className={styles.panel}>
            <summary className={styles.panelSummary}>
              <span>Atributos</span>
              <span className={styles.panelMeta}>
                {(attributes.length + specificationAttributes.length) > 0 ? `${attributes.length + specificationAttributes.length}` : 'Opcional'}
              </span>
            </summary>
            <div className={styles.panelContent}>
              <div className={styles.subSection}>
                <div className={styles.subHeader}>
                  <h4 className={styles.subTitle}>Personalizados</h4>
                  <button
                    type="button"
                    className={styles.attrButton}
                    onClick={() => setShowAttrModal(true)}
                  >
                    Gestionar
                  </button>
                </div>
                {attributes.length > 0 && (
                  <div className={styles.attributesList}>
                    {attributes.map((attr, idx) => (
                      <div key={idx} className={styles.attributeItem}>
                        <strong>{attr.name}:</strong> {attr.value}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className={styles.subSection}>
                <div className={styles.subHeader}>
                  <h4 className={styles.subTitle}>Informativos</h4>
                  <button
                    type="button"
                    className={styles.addAttrBtn}
                    onClick={addSpecificationAttribute}
                  >
                    + Agregar
                  </button>
                </div>
                {allAttributes.length === 0 && (
                  <div className={styles.noticeWarn}>
                    No se encontraron atributos. Crea algunos en "Administrador de Atributos".
                  </div>
                )}
                {specificationAttributes.length > 0 && (
                  <div className={styles.attributeRows}>
                    {specificationAttributes.map((spec, idx) => (
                      <div key={idx} className={styles.attributeRowSmall}>
                        <div>
                          <label className={styles.label}>
                            Atributo
                            <select
                              value={spec.attributeId}
                              onChange={(e) => updateSpecificationAttribute(idx, 'attributeId', e.target.value)}
                              className={styles.select}
                            >
                              <option value="">Selecciona atributo</option>
                              {allAttributes && allAttributes.filter(attr => attr.category === 'specification').length > 0 ? (
                                allAttributes.filter(attr => attr.category === 'specification').map(attr => (
                                  <option key={attr.id} value={attr.id}>{attr.name}</option>
                                ))
                              ) : (
                                <option disabled>No hay atributos informativos</option>
                              )}
                            </select>
                          </label>
                        </div>
                        <div>
                          <label className={styles.label}>
                            Valor
                            <input
                              type="text"
                              value={spec.value}
                              onChange={(e) => updateSpecificationAttribute(idx, 'value', e.target.value)}
                              className={styles.input}
                              placeholder="Ej: 100% Algodón"
                            />
                          </label>
                        </div>
                        <div className={styles.attributeRowActions}>
                          <button
                            type="button"
                            className={styles.removeAttrBtn}
                            onClick={() => removeSpecificationAttribute(idx)}
                          >
                            Eliminar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </details>

          <details className={styles.panel}>
            <summary className={styles.panelSummary}>
              <span>Variantes</span>
              <span className={styles.panelMeta}>
                {selectedVariantAttributes.length > 0 ? `${selectedVariantAttributes.length}` : 'Opcional'}
              </span>
            </summary>
            <div className={styles.panelContent}>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>
                  Agregar atributo variante
                  <select
                    className={styles.select}
                    value=""
                    onChange={async (e) => {
                      const val = e.target.value;
                      if (val && val !== '') {
                        await toggleVariantAttribute(val);
                      }
                    }}
                  >
                    <option value="">-- Selecciona atributo --</option>
                    {allAttributes
                      .filter(attr => attr.category === 'variant' && !selectedVariantAttributes.some(sel => String(sel.attributeId) === String(attr.id)))
                      .map(attr => (
                        <option key={`attr-${attr.id}`} value={String(attr.id)}>
                          {attr.name} ({attr.type})
                        </option>
                      ))
                    }
                  </select>
                </label>
              </div>

              {selectedVariantAttributes.length > 0 && (
                <div className={styles.variantSelectedBox}>
                  <div className={styles.variantSelectedHeader}>Atributos seleccionados</div>
                  <div className={styles.variantSelectedList}>
                    {selectedVariantAttributes.map((attribute, idx) => (
                      <div key={idx} className={styles.variantSelectedItem}>
                        <span className={styles.variantSelectedName}>{attribute.name}</span>
                        <button
                          type="button"
                          className={styles.variantRemoveBtn}
                          onClick={() => setSelectedVariantAttributes(selectedVariantAttributes.filter((_, i) => i !== idx))}
                        >
                          Eliminar
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedVariantAttributes.length > 0 && (
                <div className={styles.variantTableSection}>
                  {(() => {
                    const totalVariantStock = Object.values(variantCombinationStocks).reduce((total, val) => {
                      const numVal = parseInt(val) || 0;
                      return total + numVal;
                    }, 0);
                    const baseStock = parseInt(form.stock_quantity) || 0;

                    return totalVariantStock > 0 ? (
                      <div className={`${styles.stockNotice} ${totalVariantStock > baseStock ? styles.stockWarn : styles.stockOk}`}>
                        {totalVariantStock > baseStock
                          ? `Stock de variantes (${totalVariantStock}) excede el stock base (${baseStock})`
                          : `Stock total de variantes: ${totalVariantStock} / ${baseStock}`}
                      </div>
                    ) : null;
                  })()}

                  <div className={styles.variantTableWrapper}>
                    <table className={styles.variantTable}>
                      <thead>
                        <tr>
                          {selectedVariantAttributes.map((attr, idx) => (
                            <th key={idx} className={styles.variantTh}>{attr.name}</th>
                          ))}
                          <th className={styles.variantTh}>Precio</th>
                          <th className={styles.variantTh}>Stock</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const generateCombinations = (attrs) => {
                            if (attrs.length === 0) return [];
                            if (attrs.length === 1) {
                              return attrs[0].options.map((opt, idx) => [{ attrIdx: 0, optIdx: idx, option: opt }]);
                            }

                            const combinations = [];
                            const restCombinations = generateCombinations(attrs.slice(1));

                            attrs[0].options.forEach((opt, optIdx) => {
                              if (restCombinations.length === 0) {
                                combinations.push([{ attrIdx: 0, optIdx, option: opt }]);
                              } else {
                                restCombinations.forEach(combo => {
                                  combinations.push([{ attrIdx: 0, optIdx, option: opt }, ...combo.map(c => ({ ...c, attrIdx: c.attrIdx + 1 }))]);
                                });
                              }
                            });

                            return combinations;
                          };

                          const combinations = generateCombinations(selectedVariantAttributes);

                          return combinations.map((combo, comboIdx) => {
                            const comboKey = combo.map(item => item.option.optionId).sort().join('-');

                            const existingStock = variantCombinationStocks[comboKey] || '';
                            const existingPrice = variantCombinationPrices[comboKey] || form.price;

                            return (
                              <tr key={comboIdx} className={styles.variantTr}>
                                {combo.map((item, cellIdx) => (
                                  <td key={cellIdx} className={styles.variantTd}>
                                    <div className={styles.variantCell}>
                                      {selectedVariantAttributes[item.attrIdx].type === 'color' && item.option.colorHex && (
                                        <div
                                          className={styles.variantSwatch}
                                          style={{ background: item.option.colorHex }}
                                        />
                                      )}
                                      <span>{item.option.displayName}</span>
                                    </div>
                                  </td>
                                ))}
                                <td className={styles.variantTd}>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={existingPrice !== form.price ? existingPrice : ''}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      const updated = { ...variantCombinationPrices };
                                      if (value && value !== '') {
                                        updated[comboKey] = parseFloat(value);
                                      } else {
                                        delete updated[comboKey];
                                      }
                                      setVariantCombinationPrices(updated);
                                    }}
                                    placeholder={`Base: $${form.price}`}
                                    className={styles.variantInput}
                                  />
                                </td>
                                <td className={styles.variantTd}>
                                  <input
                                    type="number"
                                    min="0"
                                    value={existingStock === 0 || existingStock === '0' ? '0' : existingStock}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      const updated = { ...variantCombinationStocks };
                                      if (value === '') {
                                        updated[comboKey] = '';
                                      } else if (!isNaN(value)) {
                                        updated[comboKey] = value;
                                      }
                                      setVariantCombinationStocks(updated);
                                    }}
                                    placeholder="Cantidad"
                                    className={styles.variantInput}
                                  />
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </details>

          <div className={styles.formFooter}>
            <button type="submit" disabled={loading} className={styles.submitButton}>
              {loading ? 'Guardando...' : (product ? 'Actualizar Producto' : 'Crear Producto')}
            </button>
          </div>
        </form>
      </div>
      
      {showAttrModal && (
        <ProductAttributesModal
          attributes={attributes}
          setAttributes={setAttributes}
          onClose={() => setShowAttrModal(false)}
          productId={product?.id}
        />
      )}
    </>
  );
};

export default ProductForm;
